// src/components/BulkDeepInnerSubCategoryUpload.jsx
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "../css/bulkUpload.css";

const API = process.env.REACT_APP_API_BASE || "https://rfs2.infraconsoft.net.in/api";

export default function BulkDeepInnerSubCategoryUpload({
  open,
  onClose,
  onCommitted,
  mainCode,     // required (parent)
  innerCode,    // required (inner sub link code)
  mainName,     // optional (display)
  innerName     // optional (display)
}) {
  const modalRootRef = useRef(null);
  const fileRef = useRef(null);

  const [file, setFile] = useState(null);
  const [uploadId, setUploadId] = useState(null);
  const [session, setSession] = useState(null);
  const [sampleErrors, setSampleErrors] = useState([]);
  const [stage, setStage] = useState("idle");
  const [busy, setBusy] = useState(false);

  const swalInModal = (opts) =>
    Swal.fire({ target: modalRootRef.current || document.body, zIndex: 20000, ...opts });

  useEffect(() => {
    if (!open) {
      setFile(null);
      setUploadId(null);
      setSession(null);
      setSampleErrors([]);
      setStage("idle");
      setBusy(false);
      if (fileRef.current) fileRef.current.value = null;
    }
  }, [open]);

  const downloadTemplate = () =>
    window.open(`${API}/itemDeepInnerSubCat/bulk/template`, "_blank");

  const onPickFile = (e) => setFile(e.target.files?.[0] || null);

  // 1) Upload & stage
  const upload = async () => {
    if (!file) return swalInModal({ icon: "warning", title: "No file", text: "Choose a CSV file." });
    if (!mainCode || !innerCode)
      return swalInModal({ icon: "warning", title: "Missing parent", text: "mainCode and innerCode are required." });

    setBusy(true); setStage("uploading");
    try {
      const fd = new FormData();
      fd.append("file", file);

      const { data } = await axios.post(
        `${API}/itemDeepInnerSubCat/bulk/upload`,
        fd,
        {
          params: { mainCode, innerCode },
          headers: { "Content-Type": "multipart/form-data" }
        }
      );

      setUploadId(data.upload_id);
      setStage("staged");
      swalInModal({
        icon: "success",
        title: "Uploaded",
        text: `File staged${data.stagedRowsApprox ? ` (~${data.stagedRowsApprox} rows)` : ""}.`
      });
    } catch (e) {
      console.error(e);
      setStage("failed");
      swalInModal({ icon: "error", title: "Upload failed", text: e?.response?.data?.error || e.message });
    } finally { setBusy(false); }
  };

  // 2) Validate
  const validate = async () => {
    if (!uploadId) return swalInModal({ icon: "warning", title: "No upload", text: "Upload a file first." });

    setBusy(true); setStage("validating");
    try {
      const { data } = await axios.post(
        `${API}/itemDeepInnerSubCat/bulk/validate`,
        null,
        { params: { upload_id: uploadId, mainCode, innerCode } }
      );

      setSession(data.session || null);
      setSampleErrors(data.sampleErrors || []);
      setStage("validated");

      const total = data.session?.total_rows ?? 0;
      const valid = data.session?.valid_rows ?? 0;
      const invalid = data.session?.invalid_rows ?? 0;

      swalInModal({
        icon: invalid > 0 ? "info" : "success",
        title: "Validation complete",
        html: `
          <div style="text-align:left">
            <div><b>Main:</b> ${mainCode}${mainName ? ` (${mainName})` : ""}</div>
            <div><b>Inner Sub:</b> ${innerCode}${innerName ? ` (${innerName})` : ""}</div>
            <div><b>Total:</b> ${total}</div>
            <div><b>Valid:</b> ${valid}</div>
            <div><b>Invalid:</b> ${invalid}</div>
          </div>
        `
      });
    } catch (e) {
      console.error(e);
      setStage("failed");
      swalInModal({ icon: "error", title: "Validation failed", text: e?.response?.data?.error || e.message });
    } finally { setBusy(false); }
  };

  // 3) Commit
  const commit = async () => {
    if (!uploadId) return swalInModal({ icon: "warning", title: "No upload", text: "Upload & validate first." });

    setBusy(true); setStage("committing");
    try {
      const { data } = await axios.post(
        `${API}/itemDeepInnerSubCat/bulk/commit`,
        null,
        { params: { upload_id: uploadId, mainCode, innerCode } }
      );

      setSession(data.session || null);
      setStage("committed");
      swalInModal({ icon: "success", title: "Committed", text: "Deep inner sub categories inserted." });
      onCommitted?.();
      onClose?.();
    } catch (e) {
      console.error(e);
      setStage("failed");
      swalInModal({ icon: "error", title: "Commit failed", text: e?.response?.data?.error || e.message });
    } finally { setBusy(false); }
  };

  // Optional: refresh status
  const refreshStatus = async () => {
    if (!uploadId) return;
    try {
      const { data } = await axios.get(`${API}/itemDeepInnerSubCat/bulk/status`, { params: { upload_id: uploadId } });
      setSession(data.session || null);
      setSampleErrors(data.sampleErrors || []);
    } catch (e) {
      console.error(e);
      swalInModal({ icon: "error", title: "Status error", text: e?.response?.data?.error || e.message });
    }
  };

  const downloadErrors = () => {
    if (!uploadId) return;
    window.open(`${API}/itemDeepInnerSubCat/bulk/errors.csv?upload_id=${uploadId}`, "_blank");
  };

  if (!open) return null;

  return (
    <div ref={modalRootRef} className="bu-overlay">
      <div className="bu-card">
        <div className="bu-header">
          <div className="bu-title">
            Bulk Upload — Deep Inner Sub Categories
            <div style={{ fontSize: 12, opacity: .8, marginTop: 4 }}>
              <b>Main:</b> {mainCode}{mainName ? ` (${mainName})` : ""} &nbsp; | &nbsp;
              <b>Inner Sub:</b> {innerCode}{innerName ? ` (${innerName})` : ""}
            </div>
          </div>
          <button onClick={onClose} className="bu-close" aria-label="Close">✕</button>
        </div>

        <div className="bu-body">
          <div className="bu-info">
            <div className="bu-note">
              CSV header required: <code>itemName</code>
            </div>
            <button className="bu-template-btn" onClick={downloadTemplate}>Download Template</button>
          </div>

          <div className="bu-upload">
            <div className="bu-input-row">
              <input
                type="file"
                ref={fileRef}
                onChange={onPickFile}
                accept=".csv"
                className="bu-file"
                disabled={busy}
              />
            </div>
            <div className="bu-file-note">
              Select a .csv with the first column named <b>itemName</b>.
            </div>

            <div className="bu-actions">
              <button className="btn" onClick={upload} disabled={busy || !file}>
                {stage === "uploading" ? "Uploading…" : "Upload & Stage"}
              </button>
              <button className="btn" onClick={validate} disabled={busy || !uploadId}>
                {stage === "validating" ? "Validating…" : "Validate"}
              </button>
              <button className="btn" onClick={refreshStatus} disabled={busy || !uploadId}>
                Refresh Status
              </button>
              <button
                className="btn btn-primary"
                onClick={commit}
                disabled={busy || !uploadId || (session && session.invalid_rows > 0)}
                title={session?.invalid_rows > 0 ? "Fix invalid rows before committing." : ""}
              >
                {stage === "committing" ? "Committing…" : "Commit Valid Rows"}
              </button>
            </div>
          </div>

          {uploadId && (
            <div className="bu-status">
              <div className="bu-status-row">
                <span className="bu-pill"><b>Upload ID:</b> {uploadId}</span>
                <span className="bu-pill"><b>Status:</b> {session?.status || stage}</span>
              </div>
              <div className="bu-status-row">
                <span className="bu-pill"><b>Total:</b> {session?.total_rows ?? "-"}</span>
                <span className="bu-pill"><b>Valid:</b> {session?.valid_rows ?? "-"}</span>
                <span className="bu-pill"><b>Invalid:</b> {session?.invalid_rows ?? "-"}</span>
              </div>
            </div>
          )}

          {sampleErrors?.length > 0 && (
            <div className="bu-errors">
              <div className="bu-errors-head">
                <h4 style={{ margin: 0, fontSize: "1rem" }}>Top Errors (first 50)</h4>
                <button className="btn" onClick={downloadErrors} disabled={!uploadId}>
                  Download All Errors
                </button>
              </div>
              <div className="bu-table-wrap">
                <table className="bu-table">
                  <thead>
                    <tr><th>Row</th><th>Error</th></tr>
                  </thead>
                  <tbody>
                    {sampleErrors.map((e, idx) => (
                      <tr key={idx}>
                        <td>{e.rownum}</td>
                        <td>{e.error_msg}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
